<template>
  <div class="w-full p-5 md:w-112">
    <p class="mb-4 mr-9 font-bold dark:text-zinc-300">Invited users will have access to shared collections and content limited to the team.</p>
    <label for="inviteMembers" class="block mb-2 dark:text-zinc-500">Invite people via email</label>
    <div class="mb-2">
      <Chips placeholder="Add People" v-model="newInvitations" pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$" />

      <p v-for="(err, idx) in errors" :key="idx" class="mt-2 text-sm text-slate-500">{{ err }}</p>
    </div>
    <div class="mb-4">
      <Button type="solid" variant="blue" icon="general.paperPlane" @click.prevent="sendInvitations" :disabled="newInvitations?.length === 0">Invite</Button>
    </div>
    <template v-if="invitedEmails.length > 0">
      <p class="mb-2 dark:text-zinc-500">Invited people</p>
      <Chips v-model="invitedEmails" mode="readonly" />
    </template>
    <div class="mb-2">
      <label for="inviteLink" class="block mb-2 dark:text-zinc-500">Or share this link with your team</label>
      <input
        type="text"
        id="inviteLink"
        @focus="(e: any) => e.target.select()"
        class="w-full px-3 overflow-y-hidden bg-white bg-no-repeat border outline-none rounded-10 dark:bg-zinc-800 dark:text-zinc-500 h-9 border-slate-300 dark:border-zinc-700 focus:border-blue-500 focus:ring focus:ring-blue-500/50 active:border-slate-600"
        :disabled="newInvitations?.length === 0"
        :value="inviteLink"
      />
    </div>
    <div class="mb-4">
      <Button type="solid" variant="blue" icon="general.copy" @click.prevent="copy()">Copy link to clipboard</Button>
    </div>
    <div v-if="showMemberDiscount" class="p-2 border border-slate-300 dark:border-zinc-700 rounded-10">
      <p class="inline-block px-2.5 py-1 mb-2 text-white rounded-full bg-gradient-to-tr from-orange-500 to-red-500">20% discount</p>
      <p class="dark:text-zinc-300">
        Invite {{ membersUntillDiscount }} more member{{ membersUntillDiscount == 2 ? 's' : '' }} and reduce your price to <s>$9.95</s> $8/member.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref, Ref, watch, computed, toRaw } from 'vue';
import { useClipboard } from '@vueuse/core';
import { useToast } from '@/composables/useToast';
import { useOrganizationStore, DISCOUNT_MAX_SEATS } from '@/store/useOrganizationStore';
import { Chips } from '@/storybook/form';
import { Button } from '@/storybook/button';

const orgStore = useOrganizationStore();
const { invitationUrl, showMemberDiscount, membersCount } = storeToRefs(orgStore);
const { success, error } = useToast();

const newInvitations: Ref<string[]> = ref([]);
const invitedEmails: Ref<string[]> = ref([]);
const errors: Ref<string[]> = ref([]);
const inviteLink = ref(invitationUrl);

const inviteString = computed(() => inviteLink.value ?? '');
const { copy, copied } = useClipboard({ source: inviteString });

const emit = defineEmits(['close']);

const sendInvitations = () => {
  orgStore
    .sendOrgInvitations(toRaw(newInvitations.value))
    .then(resp => {
      success(resp.message ? resp.message : 'Invitations sent');

      newInvitations.value = [];
      return emit('close');
    })
    .catch(e => {
      const messages = e.message;

      if (messages?.length > 0) {
        messages.forEach((e: any) => e.value.push(e.detail));
      }

      error('Cannot send invitations');
    });
};

const membersUntillDiscount = computed(() => DISCOUNT_MAX_SEATS - membersCount.value);

watch(copied, res => {
  if (!res) {
    return;
  }

  success('Invite link copied to clipboard', { icon: 'general.copy' });
});
</script>
